import { breakpoints } from '@constants/breakpoints';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

import { Box, Flex } from '../../grid';
import { Icon } from '../../icons';
import { P } from '../../typography';

// Fixed height of the banner strip. The site header offsets its `top` by this
// value (see Layout `hasTopBanner` → Header `topBannerActive`) so the absolutely
// positioned header sits below the banner instead of overlapping it.
export const GLOBAL_BANNER_HEIGHT = 56;

/**
 * GlobalBanner — top-of-page promo strip advertising the active Surprise &
 * Delight campaign. Shows a sell icon + qualifying message with a dismiss (×)
 * on the right.
 *
 * Dismissal is hybrid: callers can externally control the hidden state via
 * the `hidden` prop (e.g. persist a session flag), and the banner also tracks
 * its own internal dismissal so clicking × hides it immediately without
 * waiting for the parent to flip `hidden`.
 *
 * Mounted on the menu page, fed by the campaign teaser returned in the menu
 * response (MenuStore.surpriseAndDelightBanner).
 */
const GlobalBanner = ({ message, onDismiss, hidden }) => {
  const [internallyDismissed, setInternallyDismissed] = useState(false);

  if (hidden || internallyDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setInternallyDismissed(true);
    onDismiss();
  };

  return (
    <Wrapper
      bg="warmWhite300"
      pl="24px"
      pr={{ _: '16px', md: '24px' }}
      height={`${GLOBAL_BANNER_HEIGHT}px`}
      width="100%"
      data-testid="surprise-and-delight-global-banner"
      role="region"
      aria-label="Surprise and Delight promotion"
    >
      <Flex
        alignItems="center"
        justifyContent={{ _: 'flex-start', md: 'center' }}
        width="100%"
        height="100%"
        flexWrap="nowrap"
      >
        <IconWrapper mr="8px">
          <Icon name="coupon" width="24px" height="24px" />
        </IconWrapper>

        <Message color="black">{message}</Message>

        <DismissButton
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          data-testid="surprise-and-delight-global-banner-dismiss"
        >
          <Icon name="cross" width="16px" height="16px" />
        </DismissButton>
      </Flex>
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  box-sizing: border-box;
`;

const IconWrapper = styled(Box)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const Message = styled(P)`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: center;

  @media (min-width: ${breakpoints.md}) {
    font-size: 14px;
  }
`;

const DismissButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: 0 0 0 16px;
  flex-shrink: 0;

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.7;
  }
`;

GlobalBanner.propTypes = {
  message: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
  hidden: PropTypes.bool,
};

GlobalBanner.defaultProps = {
  hidden: false,
};

export { GlobalBanner };
